import {
    UNKNOWN_ALIAS,
    UNKNOWN_SIGNAL_TRANSLATABLE,
} from "app/pages/sales-intelligence/sub-modules/signals/constants/common";
import SignalSource from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalSource";

import TechnologyChangesType from "../../../models/technology/TechnologyChangesType";
import type TechnologySignalFilters from "../../../models/technology/TechnologySignalFilters";
import TechnologySignalSubType from "../../../models/technology/TechnologySignalSubType";
import TechnologySignalTemplate from "../../../models/technology/TechnologySignalTemplate";
import TechnologyTemplateGroup from "../../../models/technology/TechnologyTemplateGroup";
import SignalTemplatesManager from "../../../models/templates/SignalTemplatesManager";

const PERIOD_AMOUNT_THREE = 3;

export default class TechnologySignalTemplatesManager extends SignalTemplatesManager<TechnologySignalFilters> {
    getEmptyTemplate(): Readonly<TechnologySignalTemplate> {
        const { timeFrame, technologies, technologyCategories, technologySubCategories, signalSource } =
            TechnologySignalTemplate.defaultValues;

        return new TechnologySignalTemplate({
            alias: UNKNOWN_ALIAS,
            name: UNKNOWN_SIGNAL_TRANSLATABLE,
            description: UNKNOWN_SIGNAL_TRANSLATABLE,
            signalSource,
            filters: {
                period: timeFrame.period,
                periodAmount: timeFrame.periodAmount,
                technologies,
                technologyCategories,
                technologySubCategories,
            },
            lastUpdatedDate: new Date(),
        });
    }

    getDefaultTemplate(): Readonly<TechnologySignalTemplate> {
        return this.getEmptyTemplate();
    }

    getTemplates(): readonly TechnologySignalTemplate[] {
        return [
            new TechnologySignalTemplate({
                templateGroup: TechnologyTemplateGroup.Competitor,
                alias: "recently-installed-competitor",
                name: { key: "si.signals.templates.technology.recently-installed-any.name" },
                description: {
                    key: "si.signals.templates.technology.recently-installed-any.description",
                },
                signalSource: SignalSource.Platform,
                filters: {
                    type: TechnologyChangesType.Added,
                    period: TechnologySignalTemplate.defaultValues.timeFrame.period,
                    periodAmount: TechnologySignalTemplate.defaultValues.timeFrame.periodAmount,
                    technologies: TechnologySignalTemplate.defaultValues.technologies,
                    technologyCategories: TechnologySignalTemplate.defaultValues.technologyCategories,
                    technologySubCategories: TechnologySignalTemplate.defaultValues.technologySubCategories,
                },
                signalSubType: TechnologySignalSubType.AddedTechnologies,
                lastUpdatedDate: new Date(),
            }),
            new TechnologySignalTemplate({
                templateGroup: TechnologyTemplateGroup.Competitor,
                alias: "recently-removed-competitor",
                name: { key: "si.signals.templates.technology.recently-removed-any.name" },
                signalSource: SignalSource.Platform,
                description: {
                    key: "si.signals.templates.technology.recently-removed-any.description",
                },
                filters: {
                    type: TechnologyChangesType.Removed,
                    period: TechnologySignalTemplate.defaultValues.timeFrame.period,
                    periodAmount: TechnologySignalTemplate.defaultValues.timeFrame.periodAmount,
                    technologies: TechnologySignalTemplate.defaultValues.technologies,
                    technologyCategories: TechnologySignalTemplate.defaultValues.technologyCategories,
                    technologySubCategories: TechnologySignalTemplate.defaultValues.technologySubCategories,
                },
                signalSubType: TechnologySignalSubType.RemovedTechnologies,
                lastUpdatedDate: new Date(),
            }),
            new TechnologySignalTemplate({
                templateGroup: TechnologyTemplateGroup.Competitor,
                alias: "competitor-renewal-coming-up",
                name: { key: "si.signals.templates.technology.competitor-renewal-coming-up.name" },
                signalSource: SignalSource.Platform,
                description: {
                    key: "si.signals.templates.technology.competitor-renewal-coming-up.description",
                },
                templateHint: {
                    key: "si.signals.templates.technology.competitor-renewal-coming-up.hint",
                },
                signalLifecycleState: "beta",
                filters: {
                    type: TechnologyChangesType.RenewalComingUp,
                    period: TechnologySignalTemplate.defaultValues.timeFrame.period,
                    periodAmount: PERIOD_AMOUNT_THREE,
                    technologies: TechnologySignalTemplate.defaultValues.technologies,
                    technologyCategories: TechnologySignalTemplate.defaultValues.technologyCategories,
                    technologySubCategories: TechnologySignalTemplate.defaultValues.technologySubCategories,
                },
                signalSubType: TechnologySignalSubType.CompetitorRenewalComingUp,
                lastUpdatedDate: new Date(),
            }),
            new TechnologySignalTemplate({
                templateGroup: TechnologyTemplateGroup.Integration,
                alias: "recently-installed-integration",
                name: {
                    key: "si.signals.templates.technology.recently-installed-integration.name",
                },
                signalSource: SignalSource.Platform,
                description: {
                    key: "si.signals.templates.technology.recently-installed-integration.description",
                },
                filters: {
                    type: TechnologyChangesType.Added,
                    period: TechnologySignalTemplate.defaultValues.timeFrame.period,
                    periodAmount: TechnologySignalTemplate.defaultValues.timeFrame.periodAmount,
                    technologies: TechnologySignalTemplate.defaultValues.technologies,
                    technologyCategories: TechnologySignalTemplate.defaultValues.technologyCategories,
                    technologySubCategories: TechnologySignalTemplate.defaultValues.technologySubCategories,
                },
                signalSubType: TechnologySignalSubType.AddedIntegrations,
                lastUpdatedDate: new Date(),
            }),
            new TechnologySignalTemplate({
                templateGroup: TechnologyTemplateGroup.Integration,
                alias: "recently-removed-integration",
                name: { key: "si.signals.templates.technology.recently-removed-integration.name" },
                description: {
                    key: "si.signals.templates.technology.recently-removed-integration.description",
                },
                signalSource: SignalSource.Platform,
                filters: {
                    type: TechnologyChangesType.Removed,
                    period: TechnologySignalTemplate.defaultValues.timeFrame.period,
                    periodAmount: TechnologySignalTemplate.defaultValues.timeFrame.periodAmount,
                    technologies: TechnologySignalTemplate.defaultValues.technologies,
                    technologyCategories: TechnologySignalTemplate.defaultValues.technologyCategories,
                    technologySubCategories: TechnologySignalTemplate.defaultValues.technologySubCategories,
                },
                signalSubType: TechnologySignalSubType.RemovedIntegrations,
                lastUpdatedDate: new Date(),
            }),
            new TechnologySignalTemplate({
                templateGroup: TechnologyTemplateGroup.MyTechnology,
                alias: "recently-removed-my-technology",
                name: {
                    key: "si.signals.templates.technology.recently-removed-my-technology.name",
                },
                description: {
                    key: "si.signals.templates.technology.recently-removed-my-technology.description",
                },
                signalSource: SignalSource.Platform,
                filters: {
                    type: TechnologyChangesType.Removed,
                    period: TechnologySignalTemplate.defaultValues.timeFrame.period,
                    periodAmount: TechnologySignalTemplate.defaultValues.timeFrame.periodAmount,
                    technologies: TechnologySignalTemplate.defaultValues.technologies,
                    technologyCategories: TechnologySignalTemplate.defaultValues.technologyCategories,
                    technologySubCategories: TechnologySignalTemplate.defaultValues.technologySubCategories,
                },
                signalSubType: TechnologySignalSubType.RemovedMyTechnologies,
                lastUpdatedDate: new Date(),
            }),
        ];
    }
}
